import {
  isProblemError,
  isValidationError,
  type KitDesign,
  type KitPut,
  type KitRead,
} from '@volee/api-client'

import type { UseFormReturn } from 'react-hook-form'

import { validationFieldErrors } from './form-errors.ts'
import { errorMessage } from '../messages/from-error.ts'
import { translateError } from '../messages/errors.ts'

// A kit's form↔API translation, testable without mounting, for every form that edits one.

/** The fields as the form holds them: `''` for a colour not chosen yet. */
export interface KitFields {
  readonly design: KitDesign
  readonly primaryColor: string
  readonly secondaryColor: string
  readonly shortsColor: string
}

/** A kit nobody has declared: solid, the plainest design and the mockup's preselected card. */
export const EMPTY_KIT_FIELDS: KitFields = {
  design: 'solid',
  primaryColor: '',
  secondaryColor: '',
  shortsColor: '',
}

/** Every design but solid carries a second colour, so only those ask for it. */
export function wantsSecondColor(design: KitDesign): boolean {
  return design !== 'solid'
}

/** The name of one of a kit's colour fields: every field but the design. */
export type KitColourName = Exclude<keyof KitFields, 'design'>

const ALL_COLOURS: readonly KitColourName[] = ['primaryColor', 'secondaryColor', 'shortsColor']

/** The colours a design asks for, in the order the form lists them and the photo takes them. */
export function coloursOf(design: KitDesign): readonly KitColourName[] {
  return wantsSecondColor(design)
    ? ['primaryColor', 'secondaryColor', 'shortsColor']
    : ['primaryColor', 'shortsColor']
}

/** A kit's colours as the API answers them, whether a società's kit or a match kit's copy. */
export type KitColours = Omit<KitRead, 'rank'>

export function fromKit(kit: KitColours | null): KitFields {
  if (kit === null) return EMPTY_KIT_FIELDS
  return {
    design: kit.design,
    primaryColor: kit.primary_color,
    secondaryColor: kit.secondary_color ?? '',
    shortsColor: kit.shorts_color,
  }
}

/** Form to a PUT body. The second colour goes only where the design shows it, and an empty
 *  one is left out for the API to refuse: whether a design needs it is the API's rule. */
export function toKitPut(fields: KitFields): KitPut {
  const body: KitPut = {
    design: fields.design,
    primary_color: fields.primaryColor.trim(),
    shorts_color: fields.shortsColor.trim(),
  }
  const secondary = fields.secondaryColor.trim()
  if (!wantsSecondColor(fields.design) || secondary === '') return body
  return { ...body, secondary_color: secondary }
}

/** The form's errors, per field. `form` is the one that belongs to no field. */
// An alias and not an interface: only aliases get the implicit index signature
// `applyFieldErrors` needs — docs/sviluppo/trappole.md § «interface e type e gli index signature».
export type KitFieldErrors = {
  readonly design?: string
  readonly primaryColor?: string
  readonly secondaryColor?: string
  readonly shortsColor?: string
  /** No field to attach it to: it goes in an Alert above the form. */
  readonly form?: string
}

/** The two colours every kit has. The body's type requires them, so they are asked for here;
 *  the second colour is not, because whether it is needed is the design's rule. */
export function missingRequired(fields: KitFields, requiredText: string): KitFieldErrors {
  const errors: Record<string, string> = {}
  if (fields.primaryColor.trim() === '') errors.primaryColor = requiredText
  if (fields.shortsColor.trim() === '') errors.shortsColor = requiredText
  return errors
}

/** Takes the focus to the first colour the errors name, in the order the form lists them:
 *  the native picker is the field, so a screen reader announces its error from there. */
export function focusFirstColourWithError(
  form: UseFormReturn<KitFields>,
  errors: KitFieldErrors,
): void {
  const first = ALL_COLOURS.find((name) => errors[name] !== undefined)
  if (first !== undefined) form.setFocus(first)
}

/** The API's field names → the form's keys. The `"body"` sentinel finds no field and lands in
 *  the Alert instead of vanishing. */
const API_FIELD_NAMES: Readonly<Record<string, keyof KitFields>> = {
  design: 'design',
  primary_color: 'primaryColor',
  secondary_color: 'secondaryColor',
  shorts_color: 'shortsColor',
}

/** A response's errors, spread over the fields they concern. A domain 422 arrives as a
 *  `ValidationError` with no fields, so the code that names a field is read first. */
export function fieldErrorsFrom(error: unknown): KitFieldErrors {
  if (isProblemError(error) && error.code === 'kit.secondary_color_required') {
    return { secondaryColor: translateError(error.code, error.params) }
  }
  if (isValidationError(error)) {
    return validationFieldErrors<KitFields>(error, API_FIELD_NAMES) ?? { form: errorMessage(error) }
  }
  return { form: errorMessage(error) }
}

/** A 403: whoever opened the page may not change these kits, whatever the page assumed. */
export function isRefused(error: unknown): boolean {
  return isProblemError(error) && error.status === 403
}

/** A refusal the società has since answered: «save the first kit first» stops being true
 *  the moment the first is saved, and an alert still saying it would be lying. */
export function isOutdated(error: unknown, firstSaved: boolean): boolean {
  return firstSaved && isProblemError(error) && error.code === 'kit.second_needs_first'
}
